// PhoneInput.jsx - Selector de teléfono con bandera, nombre de país y código internacional
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    TextField,
    InputAdornment,
    Autocomplete,
    Box,
    Typography,
    FormControl,
    FormHelperText,
} from '@mui/material';
import { Phone as PhoneIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Emoji de bandera a partir del código ISO-2 */
const getFlagEmoji = (iso2) => {
    if (!iso2 || iso2.length !== 2) return '🌍';
    try {
        return iso2.toUpperCase().replace(/./g, c =>
            String.fromCodePoint(c.charCodeAt(0) + 127397)
        );
    } catch {
        return '🌍';
    }
};

/**
 * Normaliza texto para búsqueda: elimina acentos/diacríticos, pasa a minúsculas
 * y recorta espacios. "México" → "mexico", "MÉXICO" → "mexico".
 */
const normalizeSearch = (raw) =>
    String(raw || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

/**
 * Normaliza el phoneCode: extrae solo dígitos.
 * Convierte "1 264" → "1264", "44" → "44", "" → null
 */
const normalizeCode = (raw) => {
    if (!raw && raw !== 0) return null;
    const clean = String(raw).replace(/\D/g, '');
    return clean || null;
};

/**
 * Construye la lista de países lista para usar:
 * - Filtra los que no tienen código telefónico (Bouvet Island, etc.)
 * - Normaliza el código (elimina espacios)
 * - Ordena por longitud de código DESCENDENTE para que el match sea correcto
 *   ("+52 ..." matchea México antes que cualquier subcódigo)
 */
const buildMatchingList = (raw) =>
    raw
        .map(c => ({ ...c, phoneCode: normalizeCode(c.phoneCode) }))
        .filter(c => Boolean(c.phoneCode))
        .sort((a, b) => b.phoneCode.length - a.phoneCode.length); // Longest code first → safer match

// ── Componente ───────────────────────────────────────────────────────────────

const PhoneInput = ({
    value = '',
    onChange,
    country = '',
    selectedCountry: selectedCountryProp = '',
    onCountryChange,
    error = false,
    helperText = '',
    required = false,
    disabled = false,
    label: labelProp,
    size = 'small',
}) => {
    const { t, i18n } = useTranslation();
    const label = labelProp ?? t('hardcoded.components_common_phoneinput.telefono');
    const controlledCountry = country || selectedCountryProp || '';
    const [rawCountries, setRawCountries] = useState([]);   // normalized + filtered
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loading, setLoading] = useState(true);
    const [parsed, setParsed] = useState(false);            // flag: value already parsed

    // ── 1. Cargar países ─────────────────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch('/countries-master/json/codcountries.json');
                const data = await res.json();
                setRawCountries(buildMatchingList(data));
            } catch {
                setRawCountries(buildMatchingList([
                    { nameES: t('hardcoded.components_common_phoneinput.mexico'), nameEN: t('hardcoded.components_common_phoneinput.mexico'), iso2: 'MX', iso3: 'MEX', phoneCode: '52' },
                    { nameES: t('hardcoded.components_common_phoneinput.estados_unidos'), nameEN: t('hardcoded.components_common_phoneinput.united_states'), iso2: 'US', iso3: 'USA', phoneCode: '1' },
                    { nameES: t('hardcoded.components_common_phoneinput.espana'), nameEN: 'Spain', iso2: 'ES', iso3: 'ESP', phoneCode: '34' },
                ]));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // ── 2. Parsear value existente una sola vez (cuando los países estén listos) ──
    useEffect(() => {
        if (!rawCountries.length || parsed) return;

        if (value) {
            // Eliminar todos los espacios del número para comparar
            const clean = value.replace(/\s+/g, '');

            // rawCountries ya está ordenado: código más largo primero → match seguro
            // (evita que "+1" matchee antes que "+52" si existieran ambos)
            for (const country of rawCountries) {
                if (clean.startsWith(`+${country.phoneCode}`)) {
                    setSelectedCountry(country);
                    // El número local es lo que viene DESPUÉS del código (+XX)
                    setPhoneNumber(clean.slice(country.phoneCode.length + 1));
                    setParsed(true);
                    return;
                }
            }

            // Sin match: guardar como número sin código
            setPhoneNumber(clean.replace(/^\+/, ''));
            setParsed(true);
            return;
        }

        // Sin valor previo → México por defecto
        const mx = rawCountries.find(c => c.iso2 === 'MX');
        if (mx) setSelectedCountry(mx);
        setParsed(true);
    }, [rawCountries, value, parsed]);

    // ── Lista ordenada alfabéticamente para el dropdown ──────────────────────
    const sortedOptions = useMemo(() =>
        [...rawCountries].sort((a, b) => {
            const na = i18n.language === 'es' ? a.nameES : a.nameEN;
            const nb = i18n.language === 'es' ? b.nameES : b.nameEN;
            return na.localeCompare(nb);
        }),
        [rawCountries, i18n.language]
    );

    const getCountryName = useCallback((country) =>
        i18n.language === 'es' ? country.nameES : country.nameEN,
        [i18n.language]
    );

    const findCountryByName = useCallback((name) => {
        const normalized = String(name || '').trim().toLowerCase();
        if (!normalized) return null;
        return rawCountries.find((entry) =>
            String(entry.nameES || '').trim().toLowerCase() === normalized ||
            String(entry.nameEN || '').trim().toLowerCase() === normalized ||
            String(entry.iso2 || '').trim().toLowerCase() === normalized
        ) || null;
    }, [rawCountries]);

    const normalizeCountryName = useCallback((name) => String(name || '').trim().toLowerCase(), []);

    const emitCountryChange = useCallback((entry) => {
        if (typeof onCountryChange !== 'function' || !entry) return;
        const next = entry.nameES || entry.nameEN || '';
        if (normalizeCountryName(next) === normalizeCountryName(controlledCountry)) return;
        onCountryChange(next);
    }, [onCountryChange, controlledCountry, normalizeCountryName]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleCountryChange = useCallback((_, newValue) => {
        setSelectedCountry(newValue);
        if (newValue) emitCountryChange(newValue);
        if (newValue && phoneNumber) {
            onChange(`+${newValue.phoneCode} ${phoneNumber}`);
        } else if (!newValue) {
            onChange(phoneNumber || '');
        }
    }, [phoneNumber, onChange, emitCountryChange]);

    const handlePhoneChange = useCallback((e) => {
        const digits = e.target.value.replace(/[^0-9]/g, '');
        setPhoneNumber(digits);
        if (selectedCountry && digits) {
            onChange(`+${selectedCountry.phoneCode} ${digits}`);
        } else {
            onChange(digits || '');
        }
    }, [selectedCountry, onChange]);

    useEffect(() => {
        if (!rawCountries.length || !controlledCountry) return;
        const byName = findCountryByName(controlledCountry);
        if (byName && byName.iso2 !== selectedCountry?.iso2) {
            setSelectedCountry(byName);
        }
    }, [controlledCountry, rawCountries, selectedCountry, findCountryByName]);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <FormControl fullWidth error={error}>
            <Box sx={{ display: 'flex', gap: 1 }}>

                {/* ── Selector de país con bandera ── */}
                <Autocomplete
                    options={sortedOptions}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    loading={loading}
                    disabled={disabled}
                    getOptionLabel={(o) => (o ? `+${o.phoneCode}` : '')}
                    isOptionEqualToValue={(a, b) => a.iso2 === b?.iso2}
                    filterOptions={(options, { inputValue }) => {
                        const q = normalizeSearch(inputValue).replace(/^\+/, '');
                        if (!q) return options;
                        const qDigits = q.replace(/\D/g, '');
                        return options.filter(o => {
                            const nameEs = normalizeSearch(o.nameES);
                            const nameEn = normalizeSearch(o.nameEN);
                            const iso2 = normalizeSearch(o.iso2);
                            const iso3 = normalizeSearch(o.iso3);
                            return (
                                nameEs.includes(q) ||
                                nameEn.includes(q) ||
                                iso2.startsWith(q) ||
                                iso3.startsWith(q) ||
                                (qDigits && o.phoneCode.includes(qDigits))
                            );
                        });
                    }}
                    renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.iso2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <span style={{ fontSize: '1.3rem', lineHeight: 1, flexShrink: 0 }}>
                                    {getFlagEmoji(option.iso2)}
                                </span>
                                <Box>
                                    <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                                        {getCountryName(option)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        +{option.phoneCode}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('settings.company.country')}
                            size={size}
                            placeholder={t('hardcoded.components_common_phoneinput.buscar')}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: selectedCountry ? (
                                    <InputAdornment position="start" sx={{ mr: 0.5, ml: 0.5 }}>
                                        <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>
                                            {getFlagEmoji(selectedCountry.iso2)}
                                        </span>
                                    </InputAdornment>
                                ) : params.InputProps?.startAdornment,
                            }}
                        />
                    )}
                    sx={{ minWidth: { xs: 120, sm: 132 }, flex: '0 0 auto' }}
                />

                {/* ── Campo de número ── */}
                <TextField
                    fullWidth
                    label={label}
                    size={size}
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    disabled={disabled}
                    error={error}
                    required={required}
                    placeholder={t("hardcoded.components_common_phoneinput.ejemplo_telefono")}
                    InputProps={{
                        startAdornment: selectedCountry ? (
                            <InputAdornment position="start">
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                                >
                                    +{selectedCountry.phoneCode}
                                </Typography>
                            </InputAdornment>
                        ) : null,
                        endAdornment: (
                            <InputAdornment position="end">
                                <PhoneIcon color="action" fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};

export default PhoneInput;